/* ==========================================================================
   Toe Conversion Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-toc]');
  if (!root) return;

  var MM_PER_INCH = 25.4;
  var DEG_PER_RAD = 180 / Math.PI;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  /* The toe angle satisfies tan(theta) = offset / tire diameter, where the
     offset is the front-to-rear difference measured at hub height across the
     tire. */
  function inchesToDegrees(inches, dia) {
    return Math.atan(inches / dia) * DEG_PER_RAD;
  }
  function degreesToInches(deg, dia) {
    return Math.tan(deg / DEG_PER_RAD) * dia;
  }

  function compute(input) {
    if (!isFinite(input.value)) {
      return { ok: false, errors: ['Enter a toe measurement.'] };
    }
    if (!isFinite(input.dia) || input.dia <= 0) {
      return { ok: false, errors: ['Enter the tire diameter in inches.'] };
    }

    var perWheelIn;
    var isTotal = input.basis === 'total';

    if (input.unit === 'in') {
      perWheelIn = isTotal ? input.value / 2 : input.value;
    } else if (input.unit === 'mm') {
      perWheelIn = (isTotal ? input.value / 2 : input.value) / MM_PER_INCH;
    } else if (input.unit === 'deg') {
      perWheelIn = degreesToInches(isTotal ? input.value / 2 : input.value, input.dia);
    } else { /* minutes */
      perWheelIn = degreesToInches((isTotal ? input.value / 2 : input.value) / 60, input.dia);
    }

    var perWheelDeg = inchesToDegrees(perWheelIn, input.dia);
    var totalIn  = perWheelIn * 2;
    var totalDeg = perWheelDeg * 2;

    var dir = Math.abs(totalIn) < 0.0005 ? 'zero'
            : (totalIn > 0 ? 'toe-in' : 'toe-out');

    var status;
    var absTotalIn = Math.abs(totalIn);
    if (dir === 'zero') {
      status = { key: 'good', label: 'Zero toe',
                 note: 'Wheels parallel. Lowest rolling resistance and lowest tire wear, twitchiest straight line.' };
    } else if (absTotalIn <= 0.25) {
      status = { key: 'good', label: num(totalIn, 3) + ' in ' + dir,
                 note: num(totalDeg, 3) + ' degrees total. Inside the range most road cars are specified at.' };
    } else if (absTotalIn <= 0.5) {
      status = { key: 'soon', label: num(totalIn, 3) + ' in ' + dir,
                 note: 'A large setting for a road car. Expect faster tire wear on the ' + (totalIn > 0 ? 'outer' : 'inner') + ' edges.' };
    } else {
      status = { key: 'now', label: num(totalIn, 3) + ' in ' + dir,
                 note: 'Well outside normal road specification. Check whether the spec you are reading is per wheel rather than total.' };
    }

    return {
      ok: true, errors: [], warnings: [], status: status,
      figures: [
        { label: 'Total toe', value: num(totalIn, 3) + ' in',
          sub: num(totalDeg, 3) + ' degrees, ' + num(totalDeg * 60, 1) + ' minutes, ' + num(totalIn * MM_PER_INCH, 2) + ' mm.' },
        { label: 'Per wheel', value: num(perWheelIn, 3) + ' in',
          sub: num(perWheelDeg, 3) + ' degrees, ' + num(perWheelIn * MM_PER_INCH, 2) + ' mm.' }
      ],
      rows: [
        { head: 'Total, both wheels' },
        { label: 'Inches', value: num(totalIn, 4) },
        { label: 'Millimetres', value: num(totalIn * MM_PER_INCH, 2) },
        { label: 'Degrees', value: num(totalDeg, 4) },
        { label: 'Minutes', value: num(totalDeg * 60, 1) },
        { head: 'Per wheel' },
        { label: 'Inches', value: num(perWheelIn, 4) },
        { label: 'Millimetres', value: num(perWheelIn * MM_PER_INCH, 2) },
        { label: 'Degrees', value: num(perWheelDeg, 4) },
        { label: 'Minutes', value: num(perWheelDeg * 60, 1) },
        { head: 'Reference' },
        { label: 'Tire diameter used', value: num(input.dia, 1) + ' in' },
        { label: '1/16 in total equals', value: num(inchesToDegrees(0.03125, input.dia) * 2, 3) + ' deg' },
        { label: '0.1 deg total equals', value: num(degreesToInches(0.05, input.dia) * 2, 4) + ' in' }
      ]
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "value",
          "key": "v",
          "type": "number"
      },
      {
          "name": "unit",
          "key": "u",
          "type": "select"
      },
      {
          "name": "dia",
          "key": "d",
          "type": "number"
      },
      {
          "name": "basis",
          "key": "b",
          "type": "select"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-toc__figure">'
        + '<div class="pc-toc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-toc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-toc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-toc__row pc-toc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-toc__row">'
        + '<span class="pc-toc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-toc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_TOC = { compute: compute };
})();
